import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { HeartIcon } from '../ui/HeartIcon';
import { PROMOTION_CAMPAIGN_ENABLED, promotionCampaignLinks, getCampaignCatalogHref, PromotionCampaignLink } from '../../config/promotionCampaign';

function CloseIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SearchIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
    </svg>
  );
}

function HomeIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M3 11.5l9-7 9 7M5 9.5V20a1 1 0 001 1h4v-6h4v6h4a1 1 0 001-1V9.5" />
    </svg>
  );
}

function CatalogIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
    </svg>
  );
}

function InfoIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v6M12 8h.01" />
    </svg>
  );
}

function BowtieIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M3.5 7.5l6.5 4.5-6.5 4.5v-9zM20.5 7.5L14 12l6.5 4.5v-9z" />
      <rect x="10" y="10.25" width="4" height="3.5" rx="1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} />
    </svg>
  );
}

function FlowerIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="7.5" r="2.6" strokeWidth={1.75} />
      <circle cx="12" cy="16.5" r="2.6" strokeWidth={1.75} />
      <circle cx="7.5" cy="12" r="2.6" strokeWidth={1.75} />
      <circle cx="16.5" cy="12" r="2.6" strokeWidth={1.75} />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function getPromoIcon(accent: 'boy' | 'girl', className?: string) {
  return accent === 'boy' ? <BowtieIcon className={className} /> : <FlowerIcon className={className} />;
}

function getPromoNavLinkColorClasses(accent: 'boy' | 'girl', active: boolean): string {
  if (accent === 'boy') {
    return active
      ? 'bg-olivine text-white shadow-sm'
      : 'text-olivine-dark border border-olivine bg-olivine/15 hover:bg-olivine hover:text-white';
  }
  return active
    ? 'bg-chocolate text-white shadow-sm'
    : 'text-chocolate border border-chocolate bg-champagne-light hover:bg-chocolate hover:text-white';
}

function getPromoNavIconLinkColorClasses(accent: 'boy' | 'girl', active: boolean): string {
  if (accent === 'boy') {
    return active
      ? 'bg-olivine text-white'
      : 'text-olivine-dark border border-olivine bg-olivine/15';
  }
  return active
    ? 'bg-chocolate text-white'
    : 'text-chocolate border border-chocolate bg-champagne-light';
}

function isPromoLinkActive(href: string, pathname: string, search: string): boolean {
  if (!pathname.startsWith('/catalogo')) return false;
  const targetParams = new URLSearchParams(href.split('?')[1] ?? '');
  const currentParams = new URLSearchParams(search);
  let matches = true;
  targetParams.forEach((value, key) => {
    if (currentParams.get(key) !== value) matches = false;
  });
  return matches;
}

function getPromoLinkTarget(link: PromotionCampaignLink, active: boolean): string {
  return active ? getCampaignCatalogHref() : link.href;
}

function HeaderSearch({ autoFocus, onSubmitted, wide }: { autoFocus?: boolean; onSubmitted?: () => void; wide?: boolean }) {
  const [value, setValue] = useState('');
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    navigate(trimmed ? `/catalogo?q=${encodeURIComponent(trimmed)}` : '/catalogo');
    onSubmitted?.();
  };

  const widthClasses = wide
    ? 'md:w-[240px] lg:w-[280px] xl:w-[320px]'
    : 'md:w-[200px] lg:w-[240px] xl:w-[280px]';

  return (
    <form onSubmit={handleSubmit} className={`flex min-w-0 ${widthClasses} md:mx-1`}>
      <div className="relative w-full min-w-0">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="O que você está buscando?"
          autoFocus={autoFocus}
          className="w-full min-w-0 pl-4 pr-9 py-2 rounded-full border border-champagne-dark bg-champagne-light text-text-dark placeholder-text-light focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
        />
        <button
          type="submit"
          aria-label="Buscar"
          className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full text-text-light hover:text-chocolate hover:bg-white/60 transition-colors"
        >
          <SearchIcon className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}

function NavLink({
  to,
  active,
  label,
  icon,
  variant = 'default',
  colorClassName,
}: {
  to: string;
  active: boolean;
  label: string;
  icon: React.ReactNode;
  variant?: 'default' | 'promo';
  colorClassName?: string;
}) {
  const paddingClasses = variant === 'promo' ? 'px-3 py-1.5' : 'px-4 py-2';
  const colorClasses = colorClassName ?? (active
    ? 'bg-chocolate text-white shadow-sm'
    : variant === 'promo'
      ? 'text-chocolate border border-chocolate bg-champagne-light hover:bg-chocolate hover:text-white'
      : 'text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate hover:bg-champagne-light');

  return (
    <Link
      to={to}
      className={['flex items-center gap-1.5 rounded-full text-sm font-semibold transition-all duration-200', paddingClasses, colorClasses].join(' ')}
    >
      {icon}
      {label}
    </Link>
  );
}

function NavIconLink({
  to,
  active,
  label,
  icon,
  variant = 'default',
  colorClassName,
  iconOnly,
}: {
  to: string;
  active: boolean;
  label: string;
  icon: React.ReactNode;
  variant?: 'default' | 'promo';
  colorClassName?: string;
  iconOnly?: boolean;
}) {
  const colorClasses = colorClassName ?? (active
    ? 'bg-chocolate text-white'
    : variant === 'promo'
      ? 'text-chocolate border border-chocolate bg-champagne-light'
      : 'text-text-dark hover:bg-champagne-light');

  if (iconOnly) {
    return (
      <Link
        to={to}
        aria-label={label}
        title={label}
        className={`flex items-center justify-center w-9 h-9 rounded-full transition-colors shrink-0 ${colorClasses}`}
      >
        {icon}
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors shrink-0 ${colorClasses}`}
    >
      {icon}
      {label}
    </Link>
  );
}

function CartIcon({ count }: { count: number }) {
  return (
    <Link to="/cart" aria-label="Carrinho" className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0">
      <span className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-champagne-light group-hover:bg-champagne transition-colors">
        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.4 7h12.8M7 13L5.4 5M17 21a1 1 0 11-2 0 1 1 0 012 0zm-8 0a1 1 0 11-2 0 1 1 0 012 0z" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-chocolate text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </Link>
  );
}

function FavoritesIcon({ count }: { count: number }) {
  return (
    <Link to="/favoritos" aria-label="Favoritos" className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0">
      <span className="relative flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-champagne-light group-hover:bg-champagne transition-colors">
        <HeartIcon className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-chocolate text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </span>
    </Link>
  );
}

function UserMenu({ customerName, canManageStoreSettings, onLogout }: { customerName: string | null; canManageStoreSettings: boolean; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Minha conta"
        className="group relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 shrink-0"
      >
        <span className={`flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full transition-colors ${
          open ? 'bg-champagne' : 'bg-champagne-light group-hover:bg-champagne'
        }`}>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-text-dark group-hover:text-chocolate transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-champagne py-1.5 z-50">
          {customerName && (
            <div className="px-4 pb-2 mb-1 border-b border-champagne">
              <p className="text-sm font-semibold text-text-dark truncate">{customerName}</p>
            </div>
          )}
          <Link
            to="/perfil"
            onClick={() => setOpen(false)}
            className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
              pathname === '/perfil' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Meu Perfil
          </Link>

          <Link
            to="/perfil?tab=pedidos"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-text-dark hover:bg-champagne-light transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Meus Pedidos
          </Link>

          {canManageStoreSettings && (
            <Link
              to="/configuracoes"
              onClick={() => setOpen(false)}
              className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                pathname === '/configuracoes' ? 'text-chocolate bg-champagne-light' : 'text-text-dark hover:bg-champagne-light'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Configurações
            </Link>
          )}

          <div className="border-t border-champagne my-1.5" />

          <button
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors w-full"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const totalItems = useCartStore((s) => s.totalItems());
  const totalFavorites = useFavoritesStore((s) => s.favoriteIds.length);
  const { isAuthenticated, customerName, canManageStoreSettings, logout } = useAuthStore();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    setMobileSearchOpen(false);
  }, [pathname]);

  return (
    <header className="bg-white border-b border-champagne sticky top-0 z-50">
      {/* Corpo do header */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 h-16 grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4 lg:gap-5">
        <Link to={PROMOTION_CAMPAIGN_ENABLED ? getCampaignCatalogHref() : '/'} className="flex items-center gap-1.5 sm:gap-2 group shrink-0 min-w-0">
          <img src="/images/categories/icon-7.webp" alt="" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 -mt-1" />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-brand text-xl sm:text-2xl text-chocolate-dark tracking-wide group-hover:text-chocolate transition-colors truncate">
              BabyChic
            </span>
          </div>
        </Link>

        <div className="min-w-0 overflow-hidden flex justify-center">
          <nav className="hidden lg:flex items-center gap-1.5 xl:gap-2.5 shrink-0">
            {!PROMOTION_CAMPAIGN_ENABLED && (
              <>
                <NavLink to="/" active={pathname === '/'} label="Início" icon={<HomeIcon className="w-4 h-4" />} />
                <NavLink to="/catalogo" active={pathname.startsWith('/catalogo')} label="Produtos" icon={<CatalogIcon className="w-4 h-4" />} />
                <NavLink to="/sobre" active={pathname === '/sobre'} label="Sobre" icon={<InfoIcon className="w-4 h-4" />} />
              </>
            )}
            {PROMOTION_CAMPAIGN_ENABLED && (
              <div className="flex items-center gap-1.5">
                {promotionCampaignLinks.map((link) => {
                  const active = isPromoLinkActive(link.href, pathname, search);
                  return (
                    <NavLink
                      key={link.href}
                      to={getPromoLinkTarget(link, active)}
                      active={active}
                      label={link.label}
                      icon={getPromoIcon(link.accent, 'w-4 h-4')}
                      variant="promo"
                      colorClassName={getPromoNavLinkColorClasses(link.accent, active)}
                    />
                  );
                })}
              </div>
            )}
          </nav>

          {PROMOTION_CAMPAIGN_ENABLED && (
            <div className="flex md:hidden items-center gap-1.5">
              {promotionCampaignLinks.map((link) => {
                const active = isPromoLinkActive(link.href, pathname, search);
                return (
                  <NavIconLink
                    key={link.href}
                    to={getPromoLinkTarget(link, active)}
                    active={active}
                    label={link.label}
                    icon={getPromoIcon(link.accent)}
                    variant="promo"
                    colorClassName={getPromoNavIconLinkColorClasses(link.accent, active)}
                    iconOnly
                  />
                );
              })}
            </div>
          )}

          {PROMOTION_CAMPAIGN_ENABLED && (
            <div className="hidden md:flex lg:hidden items-center gap-1.5">
              {promotionCampaignLinks.map((link) => {
                const active = isPromoLinkActive(link.href, pathname, search);
                return (
                  <NavIconLink
                    key={link.href}
                    to={getPromoLinkTarget(link, active)}
                    active={active}
                    label={link.label}
                    icon={getPromoIcon(link.accent)}
                    variant="promo"
                    colorClassName={getPromoNavIconLinkColorClasses(link.accent, active)}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="hidden md:flex min-w-0">
            <HeaderSearch wide={PROMOTION_CAMPAIGN_ENABLED} />
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1 lg:gap-1.5 shrink-0">
            {isAuthenticated ? (
              !PROMOTION_CAMPAIGN_ENABLED && (
                <UserMenu customerName={customerName} canManageStoreSettings={canManageStoreSettings} onLogout={handleLogout} />
              )
            ) : (
              <>
                <span className="hidden xl:block text-sm text-text-medium mr-1">Olá, bem-vindo(a)!</span>
                <Link
                  to="/cadastro"
                  className="hidden sm:block text-sm font-medium text-text-medium border border-champagne-dark hover:border-chocolate hover:text-chocolate px-4 py-2 rounded-full transition-colors"
                >
                  Cadastre-se
                </Link>
                {!PROMOTION_CAMPAIGN_ENABLED && (
                  <Link
                    to="/login"
                    className="text-sm font-medium text-white bg-chocolate hover:bg-chocolate-dark px-3 py-1.5 sm:px-5 sm:py-2 rounded-full transition-colors whitespace-nowrap"
                  >
                    Entrar
                  </Link>
                )}
              </>
            )}

            {!PROMOTION_CAMPAIGN_ENABLED && <FavoritesIcon count={totalFavorites} />}

            <button
              type="button"
              onClick={() => setMobileSearchOpen((v) => !v)}
              aria-label="Buscar"
              aria-expanded={mobileSearchOpen}
              className={`md:hidden flex items-center justify-center w-9 h-9 rounded-full transition-colors shrink-0 ${
                mobileSearchOpen ? 'bg-chocolate text-white' : 'text-text-dark hover:bg-champagne-light'
              }`}
            >
              {mobileSearchOpen ? <CloseIcon className="w-4 h-4" /> : <SearchIcon className="w-5 h-5" />}
            </button>

            <CartIcon count={totalItems} />
          </div>
        </div>
      </div>

      {!PROMOTION_CAMPAIGN_ENABLED && (
        <nav className="lg:hidden border-t border-champagne px-3 py-1.5 flex items-center justify-center gap-1 overflow-x-auto">
          <NavIconLink to="/" active={pathname === '/'} label="Início" icon={<HomeIcon />} />
          <NavIconLink to="/catalogo" active={pathname.startsWith('/catalogo')} label="Produtos" icon={<CatalogIcon />} />
          <NavIconLink to="/sobre" active={pathname === '/sobre'} label="Sobre" icon={<InfoIcon />} />
        </nav>
      )}

      {mobileSearchOpen && (
        <div className="md:hidden border-t border-champagne bg-white px-3 py-3">
          <HeaderSearch autoFocus onSubmitted={() => setMobileSearchOpen(false)} />
        </div>
      )}
    </header>
  );
}
